import type { ReactNode } from 'react'
import { useMemo } from 'react'
import {
  faBalanceScale,
  faFlagCheckered,
  faHistory,
  faSeedling,
  faTachometerAlt,
  faTachometerAltFastest,
} from '@fortawesome/pro-regular-svg-icons'

import RadioCard from '../../RadioCard'
import type {
  TNewUploadManualTuneRequired,
  TVehicleFuel,
} from '../../../hooks/useNewUpload'

type Props = {
  fuel: TVehicleFuel
  selected: TNewUploadManualTuneRequired | undefined
  setSelected: (value: TNewUploadManualTuneRequired) => void
  tuneRequiredError: string | null
}

type TContainerProps = {
  children: ReactNode
}

const TuneRequiredContainer = (props: TContainerProps) => {
  const { children } = props

  return (
    <div className="space-y-2 md:grid md:grid-cols-2 md:gap-2 md:space-y-0 lg:grid-cols-3">
      {children}
    </div>
  )
}

const BuildManualTune = (props: Props) => {
  const { fuel, selected, setSelected, tuneRequiredError } = props

  const tuneTypes = useMemo(() => {
    const availableTypes: Array<{
      description: string
      icon: typeof faHistory
      label: string
      value: TNewUploadManualTuneRequired
    }> = [
      {
        label: 'Stock',
        description: 'Request a stock ECU file',
        icon: faHistory,
        value: 'STOCK',
      },
      {
        label: 'Eco (diesels)',
        description: 'Tune for fuel efficiency, up to 20% saving',
        icon: faSeedling,
        value: 'ECO',
      },
      {
        label: 'Blend (diesels)',
        description: 'Hybrid of more power and more MPG',
        icon: faBalanceScale,
        value: 'BLEND',
      },
      {
        label: 'Stage 1',
        description: 'Max performance with no hardware mods',
        icon: faTachometerAlt,
        value: 'STAGE1',
      },
      {
        label: 'Stage 2',
        description: 'For decat, intake and intercooler upgrades',
        icon: faTachometerAltFastest,
        value: 'STAGE2',
      },
      {
        label: 'Stage 3 (POA)',
        description: 'For hybrid turbos, data logs must be provided',
        icon: faFlagCheckered,
        value: 'STAGE3',
      },
    ]

    if (fuel !== 'DIESEL') {
      return availableTypes.filter(
        (type) => !['ECO', 'BLEND'].includes(type.value),
      )
    }

    return availableTypes
  }, [fuel])

  return (
    <div>
      <p className="mb-2 block text-sm font-medium leading-5 text-black">
        Tune required
      </p>
      <RadioCard
        label="Tune required"
        selected={selected}
        setSelected={(value) =>
          setSelected(value as TNewUploadManualTuneRequired)
        }
        Container={TuneRequiredContainer}
        items={tuneTypes}
      />
      {tuneRequiredError && (
        <p className="mt-2 text-sm text-red-600">{tuneRequiredError}</p>
      )}
    </div>
  )
}

export default BuildManualTune
